import { useEffect, useRef } from "react";
import posthog from "posthog-js";


// Publishable PostHog project key — safe to ship in the client bundle.
const POSTHOG_KEY = "phc_mVsEk5WUkhqMdvdSWiJF3f6ta8GvfjtMmxwxEuNm9paB";
const POSTHOG_HOST = "https://us.i.posthog.com";

let initialized = false;

export function initPostHog() {
  if (initialized || typeof window === "undefined") return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
    },
  });
  initialized = true;
}

export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>,
) {
  if (!initialized) return;
  posthog.identify(userId, properties);
}

export function setPersonProperties(properties: Record<string, unknown>) {
  if (!initialized) return;
  posthog.setPersonProperties(properties);
}

export function incrementPersonProperty(key: string, by = 1) {
  if (!initialized) return;
  // posthog-js exposes increment via the `people` namespace stub.
  // Fall back to setPersonProperties with a $set_once-style override if missing.
  const ph: any = posthog;
  if (ph?.people?.increment) {
    ph.people.increment(key, by);
  } else {
    // $add merges numerically on the server side.
    posthog.capture("$set", { $add: { [key]: by } });
  }
}

export function resetPostHog() {
  if (!initialized) return;
  posthog.reset();
}

/**
 * Upsell taxonomy: every event carrying a known `placement` also gets
 * `format` (popup | banner | inline | pricing) and `page`
 * (new_design | final_output | pricing) so PostHog can group by them.
 */
export const UPSELL_TAXONOMY: Record<string, { format: string; page: string }> = {
  reminder_paywall: { format: "popup", page: "final_output" },
  hard_wall: { format: "popup", page: "new_design" },
  refine_limit: { format: "popup", page: "final_output" },
  design_banner: { format: "banner", page: "new_design" },
  render_banner: { format: "banner", page: "final_output" },
  new_design: { format: "popup", page: "new_design" },
  results: { format: "popup", page: "final_output" },
  pricing: { format: "pricing", page: "pricing" },
  direct: { format: "pricing", page: "pricing" },
};

export function withUpsellTaxonomy(properties?: Record<string, unknown>) {
  const p = properties?.placement;
  if (typeof p !== "string" || !UPSELL_TAXONOMY[p]) return properties;
  return { ...UPSELL_TAXONOMY[p], ...properties };
}

export function track(event: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.capture(event, withUpsellTaxonomy(properties));
}

/**
 * Returns a stable function that coalesces rapid calls into a single
 * trailing `track()` after `delayMs`. Intended for filter/slider changes
 * where intermediate state should not spam analytics.
 */
export function useDebouncedTrack(event: string, delayMs = 400) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<Record<string, unknown> | undefined>(undefined);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  return (properties?: Record<string, unknown>) => {
    pendingRef.current = properties;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      track(event, pendingRef.current);
      timerRef.current = null;
    }, delayMs);
  };
}

/** Emit a virtual $pageview for a client-side step without changing the URL bar. */
export function capturePageview(path: string) {
  if (!initialized || typeof window === "undefined") return;
  const url = window.location.origin + path;
  posthog.capture("$pageview", {
    $current_url: url,
    $pathname: path,
    virtual: true,
  });
}

/** Track a step transition with duration spent in the previous step. */
export function trackStep(
  step: string,
  previousStep: string | null,
  timeInPreviousStepMs: number,
  flow = "app",
) {
  if (!initialized) return;
  posthog.capture("step_viewed", {
    step,
    previous_step: previousStep,
    time_in_previous_step_ms: Math.round(timeInPreviousStepMs),
    flow,
  });
}

/** Stable id for a product when no DB id is available. */
export function productItemId(p: { title?: string; source?: string }): string {
  return `${(p.title ?? "").toLowerCase().trim()}|${(p.source ?? "").toLowerCase().trim()}`;
}

/** Parse "$1,299.00" / "€39" / "199.99" into a number, or undefined. */
export function parsePriceNumber(raw?: string | null): number | undefined {
  if (!raw) return undefined;
  const n = parseFloat(String(raw).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : undefined;
}

export { posthog };

/** Classify an outbound product link by affiliate network for analytics. */
export function affiliateProps(url?: string | null): {
  is_affiliate: boolean;
  affiliate_network: "amazon" | "awin" | null;
} {
  let host = "";
  let tagged = false;
  try {
    const u = new URL(url ?? "");
    host = u.hostname.toLowerCase();
    tagged = u.searchParams.has("tag");
  } catch {
    /* not a URL */
  }
  if (host.includes("amazon.") && tagged) return { is_affiliate: true, affiliate_network: "amazon" };
  if (host.endsWith("awin1.com") || host.includes("awin")) return { is_affiliate: true, affiliate_network: "awin" };
  return { is_affiliate: false, affiliate_network: null };
}
